if (!customElements.get('pdp-product-media')) {
  class PdpProductMedia extends HTMLElement {
    constructor() {
      super();
      this.mainSwiper = null;
      this.thumbStrip = null;
      this._mainSlides = [];
      this._thumbItems = [];
      this._slidesByTab = {};
      this._thumbsByTab = {};
      this.currentFilter = 'product';
      this._restoreState = null;
    }

    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.initWhenReady();
    }

    initWhenReady() {
      if (typeof Swiper !== 'undefined') {
        this.init();
      } else {
        document.querySelector('script[src*="swiper-bundle"]')?.addEventListener('load', () => {
          if (!this.isConnected) return;
          this.init();
        });
      }
    }

    init() {
      // Re-entrancy guard: never build a second Swiper on the same wrapper.
      if (this.mainSwiper) return;
      const mainEl = this.querySelector('.pdp-media__main-swiper');
      if (!mainEl) return;

      // Every listener registered from here on is torn down together in disconnectedCallback().
      this._ac = new AbortController();
      const { signal } = this._ac;

      this.thumbStrip = this.querySelector('pdp-thumb-strip');

      // Cache all slide elements & categorise by tab
      this._mainSlides = [...mainEl.querySelectorAll('.swiper-slide')];
      this._thumbItems = this.thumbStrip ? [...this.thumbStrip.querySelectorAll('[data-thumb-index]')] : [];

      // Keep thumbs' data-tab in sync with slides' data-tab.
      // You only need to author `data-tab` on `.swiper-slide` in Liquid.
      const syncLen = Math.min(this._mainSlides.length, this._thumbItems.length);
      for (let i = 0; i < syncLen; i++) {
        const tab = this._mainSlides[i]?.dataset?.tab;
        if (tab) this._thumbItems[i].dataset.tab = tab;
      }

      // Init main swiper
      this.mainSwiper = new Swiper(mainEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 400,
        watchSlidesProgress: true,
        lazyPreloaderClass: 'pdp-preloader',
        focusableElements: 'input, select, option, textarea, button, label',
        pagination: {
          el: this.querySelector('.pdp-media__dots'),
          clickable: true,
        },
        on: {
          slideChange: (swiper) => {
            this.handleSlideChange(swiper);
            if (this.thumbStrip) this.thumbStrip.setActive?.(swiper.activeIndex);
            this._announceSlide(swiper);
          },
          touchEnd: (swiper) => {
            const t = swiper.touches;
            if (!t) return;
            const diff = t.startX - t.currentX; // positive = swipe left (next)
            const threshold = 50;
            let switched = false;
            if (swiper.isBeginning && diff < -threshold) {
              switched = this._switchToAdjacentTab('prev');
            } else if (swiper.isEnd && diff > threshold) {
              switched = this._switchToAdjacentTab('next');
            }
            if (switched) {
              swiper.allowSlideNext = false;
              swiper.allowSlidePrev = false;
              requestAnimationFrame(() => {
                swiper.allowSlideNext = true;
                swiper.allowSlidePrev = true;
              });
            }
          },
        },
      });

      // Thumb strip → main swiper sync
      if (this.thumbStrip) {
        this.thumbStrip.addEventListener('thumb:change', (e) => {
          this.mainSwiper?.slideTo(e.detail.index);
        }, { signal });
      }

      // Navigation: fully managed (no Swiper navigation module) to support cross-tab switching
      const prevBtn = this.querySelector('.pdp-media__nav-btn--prev');
      const nextBtn = this.querySelector('.pdp-media__nav-btn--next');
      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          if (!this.mainSwiper) return;
          if (this.mainSwiper.isBeginning) {
            this._switchToAdjacentTab('prev');
          } else {
            this.mainSwiper.slidePrev();
          }
        }, { signal });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          if (!this.mainSwiper) return;
          if (this.mainSwiper.isEnd) {
            this._switchToAdjacentTab('next');
          } else {
            this.mainSwiper.slideNext();
          }
        }, { signal });
      }

      // Build tab-indexed maps & apply initial filter
      this._rebuildTabMaps();
      this.filterSlides('product');
      if (this.thumbStrip) this.thumbStrip.classList.add('is-ready');

      // Tab bar filtering
      const tabBar = this.querySelector('pdp-media-tabs');
      if (tabBar) {
        tabBar.addEventListener('tab:change', (e) => {
          this.currentFilter = e.detail.value;
          this.filterSlides(e.detail.value);
        }, { signal });
      }

      // Restore the tab/slide the user was on before a transient DOM move, so a
      // reconnect does not bounce the gallery back to the first slide (destroy()
      // in disconnectedCallback() discards activeIndex).
      if (this._restoreState) {
        const { filter, index } = this._restoreState;
        this._restoreState = null;
        // init() already ran filterSlides('product'), but that does NOT update
        // currentFilter — so never gate on `filter !== currentFilter` here, or a
        // reconnect from Details/Video would leave the tab UI on the saved tab
        // while the swiper shows product slides. Restore any valid saved tab;
        // only 'product' skips the rebuild because it is already rendered.
        if (filter && this._slidesByTab[filter]?.length) {
          this.currentFilter = filter;
          if (tabBar) tabBar.value = filter;
          if (filter !== 'product') this.filterSlides(filter);
        }
        const maxIdx = Math.max(0, this.mainSwiper.slides.length - 1);
        const target = Math.min(Math.max(index || 0, 0), maxIdx);
        if (target > 0) this.mainSwiper.slideTo(target, 0);
      }

      // Parse variant media JSON (if present)
      this._variantMediaMap = null;
      const vmScript = this.querySelector('script[data-variant-media]');
      if (vmScript) {
        try {
          this._variantMediaMap = JSON.parse(vmScript.textContent);
        } catch (_) { }
      }

      // Listen for variant changes to swap media
      this._onVariantChanged = (evt) => {
        if (evt.isInit) return; // skip initial event triggered on page load
        const variant = evt.currentVariant || (evt.detail);
        if (!variant) return;

        if (this._variantMediaMap) {
          let media = this._variantMediaMap[String(variant.id)];
          if (typeof media === 'number') media = this._variantMediaMap._groups[media];
          if (media) {
            this._swapVariantMedia(media);
          } else {
            const defaultMedia = this._variantMediaMap['__default__'];
            if (defaultMedia) this._putVariantImageFirst(variant, defaultMedia);
          }
        } else {
          this._putVariantImageFirst(variant);
        }
      };
      document.addEventListener('variant:changed', this._onVariantChanged, { signal });

      // Image click → open fullscreen lightbox
      this.initImageLightbox();

      // Tab bar: show on hover, hide on leave / idle (opt-in via data-tabs-auto-hide, desktop/mouse only)
      if (this.hasAttribute('data-tabs-auto-hide') && window.luteUtil?.isMouseDevice()) {
        this._tabIdleTimer = null;
        this._tabIdleDelay = 3000;
        const mainArea = this.querySelector('.pdp-media__main');
        if (mainArea) {
          mainArea.addEventListener('mouseenter', () => this._showTabs(mainArea), { signal });
          mainArea.addEventListener('mousemove', () => this._showTabs(mainArea), { signal });
          mainArea.addEventListener('mouseleave', () => this._hideTabs(mainArea), { signal });
        }
      }
    }

    disconnectedCallback() {
      // Detach everything init() registered so a reconnect can rebuild cleanly.
      if (this._ac) {
        this._ac.abort();
        this._ac = null;
      }
      if (this._onVariantChanged) {
        document.removeEventListener('variant:changed', this._onVariantChanged);
        this._onVariantChanged = null;
      }
      if (this._tabIdleTimer) {
        clearTimeout(this._tabIdleTimer);
        this._tabIdleTimer = null;
      }
      // filterSlides() detaches every non-active slide/thumb from the DOM, so
      // these arrays are the only references left to the Details/Video content.
      // Put every cached node back before dropping the caches, otherwise the
      // reconnect's DOM scan can only rediscover the currently rendered tab.
      const mainWrapper = this.mainSwiper?.wrapperEl
        || this.querySelector('.pdp-media__main-swiper .swiper-wrapper');
      // destroy() discards activeIndex, so remember the current tab/slide and
      // replay them in init(); otherwise a transient DOM move resets to slide 0.
      this._restoreState = this.mainSwiper
        ? { filter: this.currentFilter, index: this.mainSwiper.activeIndex }
        : null;
      // The Swiper instance stays bound to the same wrapper across a DOM move,
      // so a reconnect used to stack a second instance and reset to slide 0.
      if (this.mainSwiper) {
        this.mainSwiper.destroy(true, true);
        this.mainSwiper = null;
      }
      // Re-attach after teardown: the captured wrapper element outlives the
      // instance, and reconnect's querySelectorAll() must find every slide again.
      if (mainWrapper && this._mainSlides.length) mainWrapper.append(...this._mainSlides);
      if (this.thumbStrip && this._thumbItems.length) this.thumbStrip.append(...this._thumbItems);
      this.thumbStrip = null;
      this._mainSlides = [];
      this._thumbItems = [];
      this._slidesByTab = {};
      this._thumbsByTab = {};
      this._init = false;
    }

    /** Announce current slide for screen readers */
    _announceSlide(swiper) {
      const announcer = this.querySelector('[data-slide-announcer]');
      if (!announcer) return;
      const total = swiper.slides.length;
      const current = swiper.activeIndex + 1;
      const slide = swiper.slides[swiper.activeIndex];
      const alt = slide?.querySelector('img')?.alt || '';
      announcer.textContent = `Image ${current} of ${total}${alt ? ': ' + alt : ''}`;
    }

    _showTabs(el) {
      clearTimeout(this._tabIdleTimer);
      el.classList.add('is-tabs-visible');
      this._tabIdleTimer = setTimeout(() => this._hideTabs(el), this._tabIdleDelay);
    }

    _hideTabs(el) {
      clearTimeout(this._tabIdleTimer);
      el.classList.remove('is-tabs-visible');
    }

    /** Returns ordered array of visible tab values, e.g. ['product','details','video'] */
    _getVisibleTabs() {
      const tabBar = this.querySelector('pdp-media-tabs');
      if (!tabBar) return [this.currentFilter];
      return [...tabBar.querySelectorAll('button[data-value]:not([hidden])')].map((b) => b.dataset.value);
    }

    /** Returns the adjacent visible tab name in the given direction, or null if at boundary */
    _getAdjacentTab(direction) {
      const tabs = this._getVisibleTabs();
      const idx = tabs.indexOf(this.currentFilter);
      if (idx === -1) return null;
      return direction === 'next' ? (tabs[idx + 1] || null) : (tabs[idx - 1] || null);
    }

    /** Switch to an adjacent tab. direction: 'prev' | 'next' */
    _switchToAdjacentTab(direction) {
      const targetTab = this._getAdjacentTab(direction);
      if (!targetTab) return false;

      const goToEnd = direction === 'prev';
      const tabBar = this.querySelector('pdp-media-tabs');
      if (tabBar) tabBar.value = targetTab;
      this.currentFilter = targetTab;
      this.filterSlides(targetTab, goToEnd);
      return true;
    }

    /** Manually manage disabled/hidden state on prev/next nav btns */
    _updateNavBtns() {
      if (!this.mainSwiper) return;
      const prevBtn = this.querySelector('.pdp-media__nav-btn--prev');
      const nextBtn = this.querySelector('.pdp-media__nav-btn--next');
      if (!prevBtn || !nextBtn) return;

      const totalSlides = this.mainSwiper.slides.length;
      const tabs = this._getVisibleTabs();
      // Hide both buttons when only 1 slide and 1 tab (nothing to navigate)
      const shouldLock = totalSlides <= 1 && tabs.length <= 1;
      prevBtn.classList.toggle('swiper-button-lock', shouldLock);
      nextBtn.classList.toggle('swiper-button-lock', shouldLock);
      if (shouldLock) return;

      const isFirst = this.mainSwiper.isBeginning;
      const isLast = this.mainSwiper.isEnd;
      const hasPrevTab = this._getAdjacentTab('prev') !== null;
      const hasNextTab = this._getAdjacentTab('next') !== null;

      // Disable prev when at first slide AND no previous tab
      prevBtn.classList.toggle('swiper-button-disabled', isFirst && !hasPrevTab);
      prevBtn.toggleAttribute('aria-disabled', isFirst && !hasPrevTab);
      // Disable next when at last slide AND no next tab
      nextBtn.classList.toggle('swiper-button-disabled', isLast && !hasNextTab);
      nextBtn.toggleAttribute('aria-disabled', isLast && !hasNextTab);
    }

    /** Rebuild tab-indexed lookup maps from flat _mainSlides / _thumbItems arrays. */
    _rebuildTabMaps() {
      this._slidesByTab = {};
      this._thumbsByTab = {};
      for (const s of this._mainSlides) {
        const tab = s.dataset.tab || 'product';
        (this._slidesByTab[tab] ??= []).push(s);
      }
      for (const t of this._thumbItems) {
        const tab = t.dataset.tab || 'product';
        (this._thumbsByTab[tab] ??= []).push(t);
      }
    }

    filterSlides(filter, goToEnd = false) {
      if (!this.mainSwiper) return;

      const slides = this._slidesByTab[filter] || [];
      const thumbs = this._thumbsByTab[filter] || [];

      // Swiper API: clear + batch append (single internal update, no manual DOM)
      this.mainSwiper.removeAllSlides();
      if (slides.length) this.mainSwiper.appendSlide(slides);

      const count = this.mainSwiper.slides.length;
      const targetIdx = goToEnd && count > 0 ? count - 1 : 0;
      this.mainSwiper.slideTo(0, 0, false);
      this.mainSwiper.slideTo(targetIdx, 0);

      // Sync thumb strip
      if (this.thumbStrip) {
        const frag = document.createDocumentFragment();
        thumbs.forEach((t, i) => {
          t.dataset.thumbIndex = i;
          frag.appendChild(t);
        });
        while (this.thumbStrip.firstChild) this.thumbStrip.firstChild.remove();
        this.thumbStrip.appendChild(frag);
        this.thumbStrip.setActive?.(targetIdx, goToEnd, false);
      }

      this._updateNavBtns();
    }

    /* ── Variant Media Swap ── */

    _swapVariantMedia(media) {
      // Pause any existing videos
      this.querySelectorAll('.pdp-media__main-swiper video').forEach((v) => {
        if (!v.paused) v.pause();
      });

      // Build new slides & thumbs
      const newSlides = [];
      const newThumbs = [];
      let idx = 0;

      media.product.forEach((item) => {
        newSlides.push(this._createImageSlide(item, 'product', 'image'));
        newThumbs.push(this._createThumbBtn('product', 'image', item.thumb, item.alt, idx));
        idx++;
      });
      media.details.forEach((item) => {
        newSlides.push(this._createImageSlide(item, 'details', 'details'));
        newThumbs.push(this._createThumbBtn('details', 'image', item.thumb, item.alt, idx));
        idx++;
      });
      media.video.forEach((item) => {
        newSlides.push(this._createVideoSlide(item));
        newThumbs.push(this._createThumbBtn('video', 'video', item.thumb || item.poster, '', idx));
        idx++;
      });

      // Remove old elements
      this._mainSlides.forEach((s) => s.remove());
      this._thumbItems.forEach((s) => s.remove());

      // Store new references & rebuild tab maps
      this._mainSlides = newSlides;
      this._thumbItems = newThumbs;
      this._rebuildTabMaps();

      // Update tab visibility
      const tabBar = this.querySelector('pdp-media-tabs');
      if (tabBar) {
        const detailsBtn = tabBar.querySelector('[data-value="details"]');
        const videoBtn = tabBar.querySelector('[data-value="video"]');
        if (detailsBtn) detailsBtn.hidden = media.details.length === 0;
        if (videoBtn) videoBtn.hidden = media.video.length === 0;
        tabBar._checkVisibility();
      }

      // Reset to product tab
      this.currentFilter = 'product';
      if (tabBar) tabBar.value = 'product';
      this.filterSlides('product');

    }

    _putVariantImageFirst(variant, mediaData) {
      const featuredSrc = (variant.featured_image || variant.featured_media?.preview_image)?.src;

      if (mediaData) {
        if (featuredSrc) {
          const featuredFile = featuredSrc.replace(/\?.*$/, '').split('/').pop();
          const product = mediaData.product;
          const matchIdx = product.findIndex(
            (item) => item.src.replace(/\?.*$/, '').split('/').pop() === featuredFile
          );
          if (matchIdx > 0) {
            mediaData = { ...mediaData, product: [product[matchIdx], ...product.slice(0, matchIdx), ...product.slice(matchIdx + 1)] };
          }
        }
        this._swapVariantMedia(mediaData);
        return;
      }

      if (!this.mainSwiper || !featuredSrc) return;
      // Already on the product tab showing the variant's featured image: nothing
      // to reorder, so skip filterSlides() (it unconditionally slideTo(0)).
      if (this.currentFilter === 'product' && this._featuredAlreadyFirst(variant)) return;
      const featuredFile = featuredSrc.replace(/\?.*$/, '').split('/').pop();
      const matchIdx = this._mainSlides.findIndex((s) => {
        if (s.dataset.tab !== 'product') return false;
        const img = s.querySelector('img');
        return img && img.src.replace(/\?.*$/, '').split('/').pop() === featuredFile;
      });
      if (matchIdx > 0) {
        const [slide] = this._mainSlides.splice(matchIdx, 1);
        this._mainSlides.unshift(slide);
        if (this._thumbItems[matchIdx]) {
          const [thumb] = this._thumbItems.splice(matchIdx, 1);
          this._thumbItems.unshift(thumb);
        }
        this._rebuildTabMaps();
      }
      // Only rebuild/reset when the slide order actually changed or we must
      // switch back to the product tab.
      const needsReset = matchIdx > 0 || this.currentFilter !== 'product';
      if (this.currentFilter !== 'product') {
        this.currentFilter = 'product';
        const tabBar = this.querySelector('pdp-media-tabs');
        if (tabBar) tabBar.value = 'product';
      }
      if (needsReset) this.filterSlides('product');
    }

    /** True when the first product slide already shows the variant's featured image. */
    _featuredAlreadyFirst(variant) {
      const featuredSrc = (variant?.featured_image || variant?.featured_media?.preview_image)?.src;
      if (!featuredSrc) return false;
      const featuredFile = featuredSrc.replace(/\?.*$/, '').split('/').pop();
      const firstProduct = this._mainSlides.find((s) => s.dataset.tab === 'product');
      const img = firstProduct?.querySelector('img');
      return !!img && img.src.replace(/\?.*$/, '').split('/').pop() === featuredFile;
    }

    _createImageSlide(item, tab, mediaType) {
      const div = document.createElement('div');
      div.className = 'swiper-slide';
      div.dataset.tab = tab;
      div.dataset.mediaType = mediaType;
      div.appendChild(this._createPreloader());
      const img = document.createElement('img');
      img.src = item.src;
      img.alt = item.alt || '';
      img.loading = 'lazy';
      div.appendChild(img);
      return div;
    }

    _createPreloader() {
      const span = document.createElement('span');
      span.className = 'pdp-preloader';
      return span;
    }

    _createVideoSlide(item) {
      const div = document.createElement('div');
      div.className = 'swiper-slide';
      div.dataset.tab = 'video';
      div.dataset.mediaType = 'video';
      const video = document.createElement('video');
      video.playsInline = true;
      video.controls = true;
      video.preload = 'metadata';
      if (item.poster) video.poster = item.poster;
      const primarySrc = (item.sources && item.sources[0]) ? item.sources[0].src : '';
      if (primarySrc) video.src = primarySrc;
      div.appendChild(video);
      return div;
    }

    _createThumbBtn(tab, mediaType, src, alt, index) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pdp-thumb-strip__item';
      btn.dataset.tab = tab;
      btn.dataset.thumbIndex = index;
      btn.dataset.mediaType = mediaType;
      btn.setAttribute('aria-label', alt || 'Image');
      const img = document.createElement('img');
      img.src = src;
      img.alt = alt || '';
      img.width = 80;
      img.height = 80;
      img.loading = 'lazy';
      img.draggable = false;
      btn.appendChild(img);
      return btn;
    }

    handleSlideChange(swiper) {
      // Pause all videos when sliding away
      const videos = this.querySelectorAll('.pdp-media__main-swiper video');
      videos.forEach((video) => {
        if (!video.paused) video.pause();
      });
      this._updateNavBtns();
    }

    /**
     * Collect lightbox-ready items from visible slides.
     * @param {Element} container - element containing .swiper-slide children
     * @param {Element} [clickedSlide] - the slide that was clicked (to resolve startIndex)
     * @returns {{ items: Array, startIndex: number }}
     */
    collectLightboxItems(container, clickedSlide) {
      const slides = [...container.querySelectorAll('.swiper-slide')];
      const items = [];
      let startIndex = 0;

      for (const s of slides) {
        if (s.dataset.mediaType === 'video') continue;
        const item = this._slideToLightboxItem(s, s.dataset.mediaType);
        if (!item) continue;

        if (s === clickedSlide) startIndex = items.length;
        items.push(item);
      }

      return { items, startIndex };
    }

    /**
     * Extract raw media data from a slide element.
     * Returns a plain descriptor — rendering is handled by PdpImageLightbox.
     * @param {Element} slide
     * @param {string} type - media type from data-media-type
     * @returns {object|null}
     */
    _slideToLightboxItem(slide, type) {
      if (type === 'image' || type === 'details') {
        const img = slide.querySelector('img');
        if (!img) return null;
        return {
          type: 'image',
          src: img.src.replace(/width=\d+/, 'width=2400'),
          thumb: img.src,
          alt: img.alt || '',
          w: img.naturalWidth || parseInt(img.getAttribute('width')) || 1200,
          h: img.naturalHeight || parseInt(img.getAttribute('height')) || 1200,
        };
      }

      if (type === 'video') {
        const video = slide.querySelector('video');
        if (!video) return null;
        const src = video.src || [...video.querySelectorAll('source')].map((s) => ({
          src: s.getAttribute('src'),
          type: s.getAttribute('type'),
        }));
        const poster = video.poster || '';
        return {
          type: 'video',
          sources: Array.isArray(src) ? src : [{ src: src, type: 'video/mp4' }],
          poster: poster,
          alt: '',
          thumb: poster,
        };
      }

      if (type === 'model') {
        const viewer = slide.querySelector('model-viewer');
        if (!viewer) return null;
        return {
          type: 'model',
          src: viewer.getAttribute('src') || '',
          poster: viewer.getAttribute('poster') || '',
          alt: viewer.getAttribute('alt') || '',
          thumb: viewer.getAttribute('poster') || '',
        };
      }

      return null;
    }

    /**
     * Load the lightbox section (if needed) and open it.
     * @param {number} startIndex
     * @param {Array} items
     */
    openLightbox(startIndex, items) {
      if (!items.length) return;

      const loader = this.querySelector('pdp-section-loader[data-section-id="pdp-image-lightbox"]');
      if (!loader) return;

      const mainEl = this.querySelector('.pdp-media__main-swiper');

      const doOpen = () => {
        if (mainEl) {
          mainEl.querySelectorAll('video').forEach((v) => { if (!v.paused) v.pause(); });
        }
        const lightbox = this.querySelector('pdp-image-lightbox');
        if (lightbox) lightbox.open(startIndex, items);
      };

      if (loader.hasAttribute('loaded')) {
        doOpen();
      } else if (!loader.hasAttribute('loading')) {
        loader.addEventListener('pdp-section:loaded', () => doOpen(), { once: true });
        loader.reload();
      }
    }

    initImageLightbox() {
      const mainEl = this.querySelector('.pdp-media__main-swiper');
      if (!mainEl) return;

      mainEl.addEventListener('click', (e) => {
        const slide = e.target.closest('.swiper-slide');
        if (!slide) return;
        const mediaType = slide.dataset.mediaType;

        if (mediaType === 'video') return;
        if (!mediaType) return;

        const { items, startIndex } = this.collectLightboxItems(mainEl, slide);

        this.openLightbox(startIndex, items);
      }, { signal: this._ac?.signal });
    }

  }

  customElements.define('pdp-product-media', PdpProductMedia);
}

if (!customElements.get('pdp-media-tabs')) {
  class PdpMediaTabs extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._slider = this.querySelector('.pdp-media-tabs__slider');
      this.addEventListener('click', this);
      this.addEventListener('keydown', this);
      this._moveSlider(this.querySelector('button[aria-selected="true"]'), false);
      this.classList.add('is-ready');
      this._checkVisibility();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this);
      this.removeEventListener('keydown', this);
    }

    handleEvent(e) {
      if (e.type === 'keydown') {
        this._handleKeydown(e);
        return;
      }
      if (e.type !== 'click') return;
      const btn = e.target.closest('button[data-value]');
      if (!btn || btn.getAttribute('aria-selected') === 'true') return;

      this._selectTab(btn);
    }

    _handleKeydown(e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const tabs = [...this.querySelectorAll('button[data-value]:not([hidden])')];
      const current = tabs.findIndex(t => t.getAttribute('aria-selected') === 'true');
      if (current === -1) return;
      e.preventDefault();
      const next = e.key === 'ArrowRight'
        ? (current + 1) % tabs.length
        : (current - 1 + tabs.length) % tabs.length;
      this._selectTab(tabs[next]);
      tabs[next].focus();
    }

    _selectTab(btn) {
      this.querySelectorAll('button[data-value]').forEach((b) => {
        b.setAttribute('aria-selected', 'false');
        b.setAttribute('tabindex', '-1');
      });
      btn.setAttribute('aria-selected', 'true');
      btn.setAttribute('tabindex', '0');
      this._moveSlider(btn, true);

      this.dispatchEvent(
        new CustomEvent('tab:change', {
          bubbles: true,
          detail: { value: btn.dataset.value },
        })
      );
    }

    _moveSlider(btn, animate) {
      if (!btn || !this._slider) return;
      if (!animate) this._slider.style.transition = 'none';
      this._slider.style.width = btn.offsetWidth + 'px';
      this._slider.style.transform = `translateX(${btn.offsetLeft}px)`;
      if (!animate) {
        this._slider.offsetHeight; /* force reflow */
        this._slider.style.transition = '';
      }
    }

    _checkVisibility() {
      const visible = this.querySelectorAll('button[data-value]:not([hidden])');
      this.closest('.pdp-media__tabs').hidden = visible.length <= 1;
    }

    get value() {
      return this.querySelector('button[aria-selected="true"]')?.dataset.value || null;
    }

    set value(v) {
      const btn = this.querySelector(`button[data-value="${v}"]`);
      if (btn && btn.getAttribute('aria-selected') !== 'true') {
        this.querySelectorAll('button[data-value]').forEach((b) => {
          b.setAttribute('aria-selected', 'false');
          b.setAttribute('tabindex', '-1');
        });
        btn.setAttribute('aria-selected', 'true');
        btn.setAttribute('tabindex', '0');
        this._moveSlider(btn, true);
      }
    }
  }

  customElements.define('pdp-media-tabs', PdpMediaTabs);
}

if (!customElements.get('pdp-coupon')) {
  class PdpCoupon extends HTMLElement {
    _readConfigAndElements() {
      const dataScript = this.querySelector('script[data-pdp-coupon-data]');
      if (!dataScript) return null;

      const config = JSON.parse(dataScript.textContent);
      const checkboxEl = this.querySelector('.pdp-coupon__checkbox-wrap');
      const inputEl = this.querySelector('[data-pdp-coupon-checkbox]');
      const wrapEl = this.querySelector('.pdp-coupon__wrap');

      if (!checkboxEl || !inputEl || !wrapEl) return null;

      this._config = config;
      this._inputEl = inputEl;
      this._wrapEl = wrapEl;
      this._checkboxEl = checkboxEl;

      return { config, inputEl, wrapEl };
    }

    async _updateValueDisplay() {
      const config = this._config;
      const labelEl = this.querySelector('[data-pdp-coupon-label]');
      if (!labelEl) return;

      if (this._discountType === 'percentage') {
        labelEl.innerHTML = config.i18n.savePercentWithCode
          .replace('__PERCENT__', this._discountValue)
          .replace('__CODE__', this._discountCode);
      } else {
        // Keep legacy formatting behavior: fmt expects cents.
        const discountValueWithCureey = await window.__pdp__config__.fmt(this._discountValue * 100);
        labelEl.innerHTML = config.i18n.saveAmountWithCode
          .replace('__AMOUNT__', discountValueWithCureey)
          .replace('__CODE__', this._discountCode);
      }
    }

    _applyDiscount() {
      if (this._discountApplied) return;
      this._discountApplied = true;

      fetch(Shopify.routes.root + 'discount/' + encodeURIComponent(this._discountCode));

      const cartCountEl = document.querySelector('[data-cart-count]');
      if (cartCountEl && parseInt(cartCountEl.textContent) > 0) {
        fetch(Shopify.routes.root + 'cart.js')
          .then(function (r) {
            return r.json();
          })
          .then(function (data) {
            const codes = (data.discount_codes || []).map(function (x) {
              return x.code;
            });
            if (!codes.includes(this._discountCode)) {
              codes.push(this._discountCode);
            }

            const cartDiscountEl = document.querySelector('[data-cart-discount]');
            if (cartDiscountEl) {
              cartDiscountEl.value = codes.join(',');
            }

            return fetch(Shopify.routes.root + 'cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ discount: codes.join(',') }),
            });
          }.bind(this));
      }

      this.dispatchEvent(
        new CustomEvent('pdp:coupon-applied', {
          bubbles: true,
          detail: { discountCode: this._discountCode, discountType: this._discountType, discountValue: this._discountValue },
        })
      );
    }

    _removeDiscount() {
      this._discountApplied = false;
      this.dispatchEvent(
        new CustomEvent('pdp:coupon-removed', {
          bubbles: true,
          detail: { discountCode: this._discountCode },
        })
      );
    }

    _updateCouponUI(newCode, newType, newValue) {
      this._discountCode = newCode;
      this._discountType = newType;
      this._discountValue = parseFloat(newValue);
      this._discountApplied = false;

      const copyBtn = this.querySelector('[data-pdp-coupon-copy]');
      if (copyBtn) copyBtn.dataset.content = newCode;

      // Keep behavior: fire and forget (original code didn't await).
      this._updateValueDisplay();

      /* 变体切换时若折扣码已勾选（_isChecked），将重新派发 pdp:coupon-applied 延迟到
         当前宏任务结束：保证 PdpPrice._onVariantChanged 先清掉上一 variant 的会员价残留、
         member-price.js 先派发新 variant 的 member 状态，折扣码再叠加，避免
         "旧 variant 的 isMemberVariant 误判为会员 UI" 而丢弃折扣码（划线价 / badge 缺失）。
         手动勾选（_bindWrapClick）与应用（_applyDiscountIfChecked）仍为同步路径。 */
      if (this._isChecked) {
        setTimeout(() => {
          if (this._isChecked) this._applyDiscount();
        }, 0);
      }
    }

    _handleVariantChange(currentVariant) {
      if (!currentVariant) return;

      this._setCheckboxVisibility(this._hiddenCheckboxVariantIds.has(String(currentVariant.id)));

      const vDiscount = this._variantDiscountMap[currentVariant.id];

      if (vDiscount) {
        this._updateCouponUI(vDiscount.code, vDiscount.type, vDiscount.value);
        this._setCouponVisibility(true);
        return;
      }

      if (this._basicDiscount.code && this._basicDiscount.value && this._basicDiscount.type) {
        this._updateCouponUI(this._basicDiscount.code, this._basicDiscount.type, this._basicDiscount.value);
        this._setCouponVisibility(true);
        return;
      }

      if (this._isChecked) {
        this._isChecked = false;
        this._inputEl.checked = false;
        this._removeDiscount();
      }
      this._setCouponVisibility(false);
    }

    _applyDiscountIfChecked() {
      if (!this._inputEl.checked) return;
      if (this._checkboxHidden) return;
      this._isChecked = true;
      this._discountApplied = false;
      this._applyDiscount();
    }

    _setCheckboxVisibility(shouldHide) {
      this._checkboxHidden = !!shouldHide;
      if (this._checkboxEl) this._checkboxEl.hidden = this._checkboxHidden;

      if (this._checkboxHidden) {
        if (this._isChecked || this._inputEl.checked) {
          this._isChecked = false;
          this._inputEl.checked = false;
          this._removeDiscount();
        }
        this._inputEl.disabled = true;
        return;
      }

      this._inputEl.disabled = false;
    }

    _setCouponVisibility(visible) {
      // 不直接操作 hidden，派发事件由 member-price.js 在 mp:ui-switched 后统一应用，
      // 避免与 _switchDiscountUI 的执行顺序竞争。
      document.dispatchEvent(new CustomEvent('pdp:coupon-visibility', {
        bubbles: true,
        detail: { visible: visible, el: this }
      }));
    }

    _bindWrapClick() {
      const inputEl = this._inputEl;
      const wrapEl = this._wrapEl;
      wrapEl.addEventListener('click', (e) => {
        if (this._checkboxHidden) return;
        // copy 按钮点击：执行勾选联动（但不阻止 copy-button-timeout 自身的复制行为）
        const isCopyBtn = !!e.target.closest('[data-pdp-coupon-copy]');
        if (!isCopyBtn && e.target !== inputEl) {
          e.preventDefault();
          inputEl.checked = !inputEl.checked;
        } else if (isCopyBtn) {
          // copy 按钮：强制勾选（若已勾则保持）
          inputEl.checked = true;
        }
        this._isChecked = inputEl.checked;
        if (this._isChecked) this._applyDiscount();
        else this._removeDiscount();
      });
    }

    _bindVariantChanged() {
      this._lastVariantId = null;
      this._onVariantChanged = (evt) => {
        if (evt.isInit) return;
        const variant = evt.currentVariant || (evt.detail && evt.detail.variant);
        if (!variant || String(variant.id) === String(this._lastVariantId)) return;
        this._lastVariantId = variant.id;
        this._handleVariantChange(variant);
      };
      document.addEventListener('variant:changed', this._onVariantChanged);

      /* 旧版 PDP（main-product section）：theme 通过 jQuery.trigger 派发 variant:changed，
         不会触发原生 addEventListener，因此需额外经 jQuery 监听。
         jQuery 3.x 的 .on 底层也是原生 addEventListener：新 PDP 的原生事件会同时走到
         两条路径，靠上面的 variant.id 去重保证只处理一次。 */
      const $ = window.jQuery_T4NT || window.jQuery;
      if ($ && $.fn && $.fn.on) {
        this._jqVariantChanged = (evt) => this._onVariantChanged(evt);
        $(document).on('variant:changed', this._jqVariantChanged);
      }
    }

    _bindPageShow() {
      this._onPageShow = (e) => {
        // bfcache: JS heap + DOM restored. Skip internal updates to avoid race conditions.
        if (e.persisted) return;

        this._applyDiscountIfChecked();
        requestAnimationFrame(() => {
          this._applyDiscountIfChecked();
          requestAnimationFrame(() => {
            this._applyDiscountIfChecked();
          });
        });
      };
      window.addEventListener('pageshow', this._onPageShow);
    }

    connectedCallback() {
      const init = this._readConfigAndElements();
      if (!init) return;

      const { config } = init;

      // State
      this._isChecked = false;
      this._discountApplied = false;
      this._discountCode = config.discountCode;
      this._discountType = config.discountType;
      this._discountValue = parseFloat(config.discountValue);

      this._basicDiscount = {
        code: config.basicDiscountCode,
        type: config.basicDiscountType,
        value: parseFloat(config.basicDiscountValue),
      };

      if (this._basicDiscount.type === 'amount' && config.crossCurrency) {
        this._basicDiscount.value = this._basicDiscount.value * parseFloat(Shopify.currency.rate);
      }

      this._variantDiscountMap = {};
      config.variantDiscounts.forEach((item) => {
        const parts = item.codeValue.split(';;;;');
        const code = parts[0];
        const valuePart = parts[1];
        let value = parseFloat(valuePart.split('--')[1]);
        const type = valuePart.includes('P--') ? 'percentage' : 'amount';
        if (type === 'amount' && config.crossCurrency) {
          value = value * parseFloat(Shopify.currency.rate);
        }
        this._variantDiscountMap[item.variantId] = { code, type, value };
      });

      this._hiddenCheckboxVariantIds = new Set(
        (config.hiddenCheckboxVariantIds || []).map((id) => String(id))
      );
      this._checkboxHidden = false;
      this._setCheckboxVisibility(this._hiddenCheckboxVariantIds.has(String(config.currentVariantId || '')));

      // Main discount conversion
      if (this._discountType === 'amount' && config.crossCurrency) {
        this._discountValue = this._discountValue * parseFloat(Shopify.currency.rate);
        this._updateValueDisplay();
      }

      this._bindWrapClick();
      this._bindVariantChanged();
      this._handleVariantChange({ id: config.currentVariantId });

      // Sync state if browser restored the checkbox (bfcache / back-forward)
      // Defer so PdpPrice has time to attach its event listeners first.
      if (this._inputEl.checked && !this._isChecked) {
        this._isChecked = true;
        setTimeout(() => this._applyDiscount(), 0);
      }

      this._bindPageShow();
    }

    disconnectedCallback() {
      if (this._onVariantChanged) {
        document.removeEventListener('variant:changed', this._onVariantChanged);
      }
      if (this._jqVariantChanged) {
        const $ = window.jQuery_T4NT || window.jQuery;
        if ($ && $.fn && $.fn.off) $(document).off('variant:changed', this._jqVariantChanged);
      }
      if (this._onPageShow) {
        window.removeEventListener('pageshow', this._onPageShow);
      }
    }
  }
  customElements.define('pdp-coupon', PdpCoupon);
}

/* ===== PDP Installment Klarna Web Component ===== */
if (!customElements.get('pdp-installment-klarna')) {
  const KLARNA_SDK_URL = 'https://js.klarna.com/web-sdk/v1/klarna.js';
  const KLARNA_READY_CALLBACK = 'pdpKlarnaSDKReady';
  let klarnaReadyPromise = null;

  function getKlarnaReadyPromise() {
    if (window.Klarna?.OnsiteMessaging?.on) return Promise.resolve(window.Klarna);

    if (!klarnaReadyPromise) {
      klarnaReadyPromise = new Promise((resolve) => {
        const previousCallback = window[KLARNA_READY_CALLBACK];
        window[KLARNA_READY_CALLBACK] = (klarna) => {
          if (typeof previousCallback === 'function') previousCallback(klarna);
          resolve(klarna || window.Klarna);
        };
      });
    }

    return klarnaReadyPromise;
  }

  function getKlarnaSDKUrl(src) {
    const url = new URL(src, window.location.href);
    url.searchParams.set('ready', KLARNA_READY_CALLBACK);
    return url.href;
  }

  class PdpInstallmentKlarna extends HTMLElement {
    static get observedAttributes() {
      return ['data-price'];
    }

    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._placement = this.querySelector('klarna-placement');
      if (!this._placement) {
        this._hideLoading();
        return;
      }

      this._syncPrice(this.getAttribute('data-price'));
      this._initPlacementWatch();
      this._loadSDK();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'data-price' || oldValue === newValue || !this._init) return;
      if (this._syncPrice(newValue)) this._refreshPlacement();
    }

    _syncPrice(value) {
      const price = parseInt(value, 10);
      if (isNaN(price) || !this._placement) return false;
      this._placement.setAttribute('data-purchase-amount', String(price));
      return true;
    }

    _loadSDK() {
      const clientId = this.dataset.clientId;
      if (!clientId || !window.luteUtil?.loadLibrary) {
        this._hideLoading();
        return;
      }

      const readyPromise = getKlarnaReadyPromise();

      window.luteUtil.loadLibrary({
        src: getKlarnaSDKUrl(this.dataset.src || KLARNA_SDK_URL),
        type: 'script',
        attrs: {
          'data-environment': this.dataset.environment || 'production',
          'data-client-id': clientId,
        },
      }).then(() => {
        return readyPromise;
      }).then(() => {
        this._initPlacementWatch();
      }).catch(() => {
        this._hideLoading();
      });
    }

    _isPlacementRendered() {
      const placement = this._placement;
      if (!placement || !placement.isConnected) return false;

      const host = placement.nextElementSibling;
      if (host?.shadowRoot?.childElementCount > 0) return true;
      if (placement.shadowRoot?.childElementCount > 0) return true;

      const { height, width } = placement.getBoundingClientRect();
      return height > 4 && width > 40;
    }

    _observePlacementRender() {
      if (this._placementObserver || !this._placement) return;

      const checkRendered = () => {
        if (this._isPlacementRendered()) this._hideLoading();
      };

      this._placementObserver = new MutationObserver(checkRendered);
      this._placementObserver.observe(this, { childList: true, subtree: true });
      checkRendered();
    }

    _refreshPlacement() {
      const refresh = window.Klarna?.OnsiteMessaging?.refresh;
      if (typeof refresh === 'function') refresh.call(window.Klarna.OnsiteMessaging);
    }

    _initPlacementWatch() {
      if (!this._placementWatchStarted) {
        this._placementWatchStarted = true;
        this._observePlacementRender();
        this._scheduleLoadingFallback();
      }

      this._refreshPlacement();
      if (this._isPlacementRendered()) this._hideLoading();
    }

    _scheduleLoadingFallback() {
      if (this._loadingFallbackTimer) window.clearTimeout(this._loadingFallbackTimer);
      this._loadingFallbackTimer = window.setTimeout(() => this._hideLoading(), 8000);
    }

    _showLoading() {
      this.classList.remove('is-ready');
      const loading = this.querySelector('[data-pdp-installment-loading]');
      if (loading) loading.classList.remove('is-hidden');
    }

    _hideLoading() {
      if (this._loadingFallbackTimer) {
        window.clearTimeout(this._loadingFallbackTimer);
        this._loadingFallbackTimer = null;
      }
      this.classList.add('is-ready');
      const loading = this.querySelector('[data-pdp-installment-loading]');
      if (loading) loading.classList.add('is-hidden');
    }

    disconnectedCallback() {
      if (this._placementObserver) {
        this._placementObserver.disconnect();
        this._placementObserver = null;
      }
      if (this._loadingFallbackTimer) {
        window.clearTimeout(this._loadingFallbackTimer);
        this._loadingFallbackTimer = null;
      }
    }
  }

  customElements.define('pdp-installment-klarna', PdpInstallmentKlarna);
}

/* ===== PDP Installment PayPal Web Component ===== */
if (!customElements.get('pdp-installment-paypal')) {
  class PdpInstallmentPaypal extends HTMLElement {
    static get observedAttributes() {
      return ['data-price'];
    }

    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._ppMsg = this.querySelector('[data-pdp-installment-paypal]');
      if (!this._ppMsg) return;

      this._loadAndRender();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'data-price' || oldValue === newValue || !this._init) return;
      const price = parseInt(newValue, 10);
      if (!isNaN(price)) this._renderMessage(price);
    }

    _loadAndRender() {
      const clientId = this.dataset.clientId;
      if (!clientId || !window.luteUtil?.loadLibrary) {
        this._hidePaypal();
        return;
      }

      window.luteUtil
        .loadLibrary({
          src: `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&components=messages`,
          type: 'script',
        })
        .then(() => {
          const price = parseInt(this.getAttribute('data-price'), 10);
          if (!isNaN(price)) this._renderMessage(price);
        })
        .catch(() => {
          this._hidePaypal();
        });
    }

    _renderMessage(priceInCents) {
      if (!this._ppMsg || typeof paypal === 'undefined' || !paypal.Messages) {
        return;
      }

      paypal
        .Messages({
          amount: priceInCents / 100,
          pageType: 'product-details',
          buyerCountry: this.dataset.country,
          locale: this.dataset.locale,
          currency: this.dataset.currency,
        })
        .render(this._ppMsg)
        .then(() => {
          const loading = this.querySelector('[data-pdp-installment-loading]');
          if (loading) loading.classList.add('is-hidden');
          const wrap = this._ppMsg.closest('.pdp-product__installment-pp-wrap');
          if (wrap) wrap.hidden = false;
        })
        .catch(() => {
          this._hidePaypal();
        });
    }

    _hidePaypal() {
      const wrap = this._ppMsg?.closest('.pdp-product__installment-pp-wrap');
      if (wrap) wrap.hidden = true;
      const loading = this.querySelector('[data-pdp-installment-loading]');
      if (loading) loading.classList.add('is-hidden');
    }

    disconnectedCallback() { }
  }

  customElements.define('pdp-installment-paypal', PdpInstallmentPaypal);
}

/* ===== PDP Collapsible Web Component ===== */
if (!customElements.get('pdp-collapsible')) {
  let _pdpCollapsibleUid = 0;

  class PdpCollapsible extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this._toggle = this.querySelector('[data-toggle]');
      this._panel = this.querySelector('.pdp-collapsible__panel');

      /* Generate unique IDs for aria-controls association */
      if (this._toggle && this._panel) {
        const uid = 'pdp-collapse-' + (++_pdpCollapsibleUid);
        this._panel.id = uid;
        this._toggle.setAttribute('aria-controls', uid);
      }

      if (this._toggle) {
        this._toggle.setAttribute('aria-expanded', String(this.open));
        this._toggle.addEventListener('click', () => this.handleToggle());
      }
    }

    handleToggle() {
      this.open = !this.open;
    }

    get open() {
      return this.hasAttribute('open');
    }

    set open(val) {
      const prev = this.open;
      if (val) {
        this.setAttribute('open', '');
      } else {
        this.removeAttribute('open');
      }
      if (this._toggle) {
        this._toggle.setAttribute('aria-expanded', String(!!val));
      }
      if (prev !== !!val) {
        this.dispatchEvent(new CustomEvent('toggle', { detail: { open: !!val }, bubbles: true }));

        if (val) {
          const parent = this.closest('[data-pdp-accordion]');
          if (parent) {
            parent.querySelectorAll('pdp-collapsible[open]').forEach((sibling) => {
              if (sibling !== this) sibling.open = false;
            });
          }
        }
      }
    }
  }

  customElements.define('pdp-collapsible', PdpCollapsible);
}



/* ===== PDP Info Button — Opens a PdpInfoModal with inline <template> content ===== */
if (!customElements.get('pdp-info-btn')) {
  class PdpInfoBtn extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._modal = null;

      if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');

      this.addEventListener('click', this);
      this.addEventListener('keydown', this);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this);
      this.removeEventListener('keydown', this);
    }

    handleEvent(e) {
      const isKeyboardOpen = e.type === 'keydown' && (e.key === 'Enter' || e.key === ' ');
      if (isKeyboardOpen) {
        e.preventDefault();
      }

      if (e.type !== 'click' && !isKeyboardOpen) return;

      const modal = this._getModal();
      this._prepareModal(modal);
      this._showModal(modal);
    }

    _getModal() {
      if (!this._modal) {
        this._modal = document.createElement('pdp-info-modal');
        this.appendChild(this._modal);
      }
      return this._modal;
    }

    _prepareModal(modal) {
      const tpl = this.querySelector('template');
      if (tpl) {
        const html = tpl.innerHTML;
        modal.setTitle(this.dataset.title || tpl.dataset.title || '');
        if (this._modalContent !== html) {
          modal.setContent(html);
          this._modalContent = html;
        }
      }
      if (this.dataset.modalClass) {
        modal.setClass(this.dataset.modalClass);
      }
    }

    _showModal(modal) {
      modal.show();
    }
  }

  customElements.define('pdp-info-btn', PdpInfoBtn);
}

if (!customElements.get('pdp-installment-modal')) {
  class PdpInstallmentModal extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._price = parseInt(this.dataset.price, 10);
      this._onLegacyVariantChanged = (e) => this._handleLegacyVariantChanged(e);
      this._onCurrencyUpdate = () => this._syncPrice();
      this.addEventListener('pdp-info-modal:toggle', this);
      document.addEventListener('pdp:price-change', this);
      document.addEventListener('variant:changed', this._onLegacyVariantChanged);
      document.addEventListener('currency:update', this._onCurrencyUpdate);
      this._bindJqueryLegacyEvents();
    }

    disconnectedCallback() {
      this.removeEventListener('pdp-info-modal:toggle', this);
      document.removeEventListener('pdp:price-change', this);
      document.removeEventListener('variant:changed', this._onLegacyVariantChanged);
      document.removeEventListener('currency:update', this._onCurrencyUpdate);
      this._unbindJqueryLegacyEvents();
    }

    handleEvent(e) {
      if (e.type === 'pdp-info-modal:toggle') {
        if (e.detail?.open && e.target.matches('pdp-info-modal')) this._syncPrice(e.target.contentElement);
      } else if (e.type === 'pdp:price-change') {
        const price = e.detail?.price;
        this._setPrice(price);
      }
    }

    _handleLegacyVariantChanged(e) {
      const variant = e.currentVariant || e.originalEvent?.currentVariant || e.detail?.variant || e.detail;
      if (!variant || variant === 'nathan') return;
      this._setPrice(variant.price);
    }

    _setPrice(price) {
      if (typeof price !== 'number' || isNaN(price)) return;
      this._price = price;
      this.dataset.price = String(this._price);
      this._syncPrice();
    }

    _bindJqueryLegacyEvents() {
      const $ = window.jQuery_T4NT || window.jQuery;
      if (!$) return;

      this._jqNamespace = `.pdpInstallment${Math.random().toString(36).slice(2)}`;
      this._$legacyVariantTarget = $(this.closest('[data-product-featured]') || document);
      this._$legacyCurrencyTargets = $(document).add(document.body);
      this._$legacyVariantTarget.on(`variant:changed${this._jqNamespace}`, this._onLegacyVariantChanged);
      this._$legacyCurrencyTargets.on(`currency:update${this._jqNamespace}`, this._onCurrencyUpdate);
    }

    _unbindJqueryLegacyEvents() {
      if (!this._jqNamespace) return;
      if (this._$legacyVariantTarget) this._$legacyVariantTarget.off(this._jqNamespace);
      if (this._$legacyCurrencyTargets) this._$legacyCurrencyTargets.off(this._jqNamespace);
      this._jqNamespace = null;
      this._$legacyVariantTarget = null;
      this._$legacyCurrencyTargets = null;
    }

    _syncPrice(content = null) {
      const price = this._price;
      if (typeof price !== 'number' || isNaN(price)) return;

      content ??= this.querySelector('pdp-info-modal')?.contentElement;
      if (!content) return;

      const priceValue = String(price);
      content.querySelectorAll('pdp-installment-klarna, pdp-installment-paypal').forEach((el) => {
        el.setAttribute('data-price', priceValue);
      });
    }
  }

  customElements.define('pdp-installment-modal', PdpInstallmentModal);
}



/* ===== PDP Guarantees Web Component ===== */
if (!customElements.get('pdp-guarantees')) {
  class PdpGuarantees extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._modal = null;
      this.addEventListener('click', this);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this);
    }

    handleEvent(e) {
      if (e.type === 'click') {
        const btn = e.target.closest('.pdp-product__guarantee-header');
        if (!btn) return;
        const item = btn.closest('.pdp-product__guarantee-item');
        if (!item) return;
        const tpl = item.querySelector('template');
        if (!tpl) return;
        this._getModal().setTitle(tpl.dataset.title || '');
        this._getModal().setContent(tpl.innerHTML);
        this._getModal().show();
      }
    }

    _getModal() {
      if (!this._modal) {
        this._modal = document.createElement('pdp-info-modal');
        this._modal.setClass('pdp-rich-text');
        this.appendChild(this._modal);
      }
      return this._modal;
    }
  }

  customElements.define('pdp-guarantees', PdpGuarantees);
}

/* ===== PDP Section Loader Web Component ===== */
if (!customElements.get('pdp-section-loader')) {
  const SPLIT_MARKER = '<!-- [pdp_section_split] -->';

  class PdpSectionLoader extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._loaded = false;

      const trigger = this.dataset.trigger;

      if (trigger) {
        this._onTriggerClick = (e) => {
          if (e.target.closest(trigger)) {
            e.preventDefault();
            this._load();
          }
        };
        document.addEventListener('click', this._onTriggerClick);
      } else if (this.hasAttribute('data-lazy')) {
        this._observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              this._observer.disconnect();
              this._load();
            }
          },
          { rootMargin: '200px' }
        );
        this._observer.observe(this);
      } else if (this.hasAttribute('data-idle')) {
        const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
        this._idleId = schedule(() => this._load(), { timeout: 3000 });
      } else if (this.children.length) {
        this._onChildClick = (e) => {
          e.preventDefault();
          this._load();
        };
        this.addEventListener('click', this._onChildClick, { once: true });
      } else {
        this._load();
      }
    }

    disconnectedCallback() {
      if (this._onTriggerClick) {
        document.removeEventListener('click', this._onTriggerClick);
      }
      if (this._onChildClick) {
        this.removeEventListener('click', this._onChildClick);
      }
      if (this._observer) {
        this._observer.disconnect();
      }
      if (this._idleId) {
        const cancel = window.cancelIdleCallback || clearTimeout;
        cancel(this._idleId);
        this._idleId = null;
      }
    }

    _load() {
      if (this._loaded || this.hasAttribute('loading')) return;

      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;

      this.setAttribute('loading', '');
      this.dispatchEvent(new CustomEvent('pdp-section:loading', { bubbles: true }));

      const baseUrl = this.dataset.url || window.location.pathname;
      const sep = baseUrl.includes('?') ? '&' : '?';
      const url = `${baseUrl}${sep}section_id=${encodeURIComponent(sectionId)}`;

      pdpFetchSection(url, { splitMarker: SPLIT_MARKER })
        .then((innerHTML) => {
          this._inject(innerHTML);
          this._loaded = true;
          this.removeAttribute('loading');
          this.setAttribute('loaded', '');
          if (this._onTriggerClick) {
            document.removeEventListener('click', this._onTriggerClick);
            this._onTriggerClick = null;
          }
          this.dispatchEvent(new CustomEvent('pdp-section:loaded', { bubbles: true }));
        })
        .catch((err) => {
          console.warn(`[pdp-section-loader] Failed to load "${sectionId}":`, err);
          this.removeAttribute('loading');
          this.setAttribute('error', '');
          this.dispatchEvent(new CustomEvent('pdp-section:error', { bubbles: true, detail: { error: err } }));
        });
    }

    _inject(raw) {
      let root;
      if (this.hasAttribute('data-replace')) {
        this.innerHTML = raw;
        root = this;
      } else {
        const prev = this.querySelector('[data-loaded-content]');
        if (prev) prev.remove();
        root = document.createElement('div');
        root.setAttribute('data-loaded-content', '');
        root.style.display = 'contents';
        root.innerHTML = raw;
        this.appendChild(root);
      }

      const sid = this.dataset.sectionId;
      root.querySelectorAll('style').forEach((s) => {
        if (!document.head.querySelector(`style[data-section="${sid}"]`)) {
          s.dataset.section = sid;
          document.head.appendChild(s);
        } else {
          s.remove();
        }
      });

      root.querySelectorAll('script').forEach((old) => {
        const s = document.createElement('script');
        for (const attr of old.attributes) s.setAttribute(attr.name, attr.value);
        s.textContent = old.textContent;
        old.replaceWith(s);
      });
    }

    reload() {
      this._loaded = false;
      this.removeAttribute('loaded');
      this.removeAttribute('error');
      this._load();
    }
  }

  customElements.define('pdp-section-loader', PdpSectionLoader);
}

/* ===== PDP Thumb Strip — Generic scrollable thumbnail strip ===== */
if (!customElements.get('pdp-thumb-strip')) {
  class PdpThumbStrip extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._activeIndex = 0;

      this._dragging = false;
      this._didDrag = false;
      this._startX = 0;
      this._scrollStart = 0;

      this.addEventListener('click', this);
      this.addEventListener('mousedown', this);
      this.addEventListener('pointerdown', this);
      this._syncActive(0, false);
      this._checkScrollable();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this);
      this.removeEventListener('mousedown', this);
      this.removeEventListener('pointerdown', this);
    }

    handleEvent(e) {
      switch (e.type) {
        case 'pointerdown':
          e.stopPropagation();
          break;
        case 'mousedown':
          if (e.button !== 0) return;
          this._dragging = true;
          this._didDrag = false;
          this._startX = e.clientX;
          this._scrollStart = this.scrollLeft;
          this.style.cursor = 'grabbing';
          this._onMouseMove ??= (ev) => this._handleMouseMove(ev);
          this._onMouseUp ??= (ev) => this._handleMouseUp(ev);
          window.addEventListener('mousemove', this._onMouseMove);
          window.addEventListener('mouseup', this._onMouseUp);
          e.preventDefault();
          break;
        case 'click': {
          if (this._didDrag) return;
          const item = e.target.closest('[data-thumb-index]');
          if (!item) return;
          const index = parseInt(item.dataset.thumbIndex, 10);
          if (isNaN(index) || index === this._activeIndex) return;
          this.setActive(index, true);
          this.dispatchEvent(new CustomEvent('thumb:change', { bubbles: true, detail: { index } }));
          break;
        }
      }
    }

    _handleMouseMove(e) {
      if (!this._dragging) return;
      const dx = e.clientX - this._startX;
      if (Math.abs(dx) > 3) this._didDrag = true;
      this.scrollLeft = this._scrollStart - dx;
    }

    _handleMouseUp() {
      this._dragging = false;
      this.style.cursor = '';
      window.removeEventListener('mousemove', this._onMouseMove);
      window.removeEventListener('mouseup', this._onMouseUp);
    }

    /** @param {number} index  @param {boolean} [scroll=true]  @param {boolean} [animate=true] */
    setActive(index, scroll = true, animate = true) {
      this._activeIndex = index;
      this._syncActive(index, scroll, animate);
    }

    get activeIndex() {
      return this._activeIndex;
    }

    /**
     * Build thumbnails from an array of {src, alt} objects.
     * Replaces children inside the scroller.
     */
    renderItems(items) {
      this.innerHTML = '';

      items.forEach((item, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pdp-thumb-strip__item';
        btn.dataset.thumbIndex = i;
        btn.setAttribute('aria-label', `Image ${i + 1}`);
        const img = document.createElement('img');
        img.src = item.src;
        img.alt = item.alt || '';
        img.loading = 'lazy';
        img.draggable = false;
        btn.append(img);
        this.append(btn);
      });

      this._syncActive(0, false);
      this._checkScrollable();
    }

    _checkScrollable() {
      this.classList.toggle('is-scrollable', this.scrollWidth > this.clientWidth);
    }

    _syncActive(index, scroll, animate = true) {
      const items = this.querySelectorAll('[data-thumb-index]');
      items.forEach((el, i) => el.classList.toggle('is-active', i === index));

      if (!scroll) return;
      const active = items[index];
      if (!active) return;
      requestAnimationFrame(() => {
        const containerRect = this.getBoundingClientRect();
        const activeRect = active.getBoundingClientRect();
        const offset = this.scrollLeft + (activeRect.left - containerRect.left) - (containerRect.width - activeRect.width) / 2;
        this.scrollTo({ left: Math.max(0, offset), behavior: animate ? 'smooth' : 'instant' });
      });
    }
  }

  customElements.define('pdp-thumb-strip', PdpThumbStrip);
}

/* ===== PDP Quantity Stepper Web Component ===== */
if (!customElements.get('pdp-qty-stepper')) {
  class PdpQtyStepper extends HTMLElement {
    static get observedAttributes() {
      return ['min', 'max', 'step', 'value', 'disabled'];
    }

    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this._minusBtn = this.querySelector('[data-action="minus"]');
      this._plusBtn = this.querySelector('[data-action="plus"]');
      this._input = this.querySelector('input');
      if (!this._input) return;

      this.addEventListener('click', this);
      this._input.addEventListener('change', this);
      this._updateState();
    }

    handleEvent(e) {
      if (e.type === 'click') {
        const btn = e.target.closest('[data-action]');
        if (!btn || this.hasAttribute('disabled')) return;
        const action = btn.dataset.action;
        const cur = parseInt(this._input.value, 10) || this._min;
        const next = action === 'minus'
          ? Math.max(this._min, cur - this._step)
          : Math.min(this._max, cur + this._step);
        if (next !== cur) {
          this._input.value = next;
          this._updateState();
          this._dispatch();
        }
      } else if (e.type === 'change') {
        let val = parseInt(this._input.value, 10);
        val = this._clamp(isNaN(val) ? this._min : val);
        this._input.value = val;
        this._updateState();
        this._dispatch();
      }
    }

    attributeChangedCallback(name) {
      if (!this._init) return;
      if (name === 'value') {
        const val = this._clamp(parseInt(this.getAttribute('value'), 10) || this._min);
        if (this._input) this._input.value = val;
      }
      if (name === 'max' || name === 'min' || name === 'step') {
        if (this._input) {
          this._input.min = this._min;
          this._input.max = this._max;
          this._input.step = this._step;
          const clamped = this._clamp(parseInt(this._input.value, 10));
          if (clamped !== parseInt(this._input.value, 10)) this._input.value = clamped;
        }
      }
      this._updateState();
    }

    get _min() { return parseInt(this.getAttribute('min'), 10) || 1; }
    get _max() { return parseInt(this.getAttribute('max'), 10) || 9999; }
    get _step() { return parseInt(this.getAttribute('step'), 10) || 1; }

    get value() { return this._input ? parseInt(this._input.value, 10) : this._min; }
    set value(v) {
      const val = this._clamp(parseInt(v, 10) || this._min);
      if (this._input) this._input.value = val;
      this._updateState();
    }

    _clamp(val) {
      return Math.max(this._min, Math.min(this._max, val));
    }

    _updateState() {
      if (!this._minusBtn || !this._plusBtn) return;
      const val = parseInt(this._input.value, 10);
      this._minusBtn.disabled = val <= this._min || this.hasAttribute('disabled');
      this._plusBtn.disabled = val >= this._max || this.hasAttribute('disabled');
      this._minusBtn.classList.toggle('is-disabled', this._minusBtn.disabled);
      this._plusBtn.classList.toggle('is-disabled', this._plusBtn.disabled);
    }

    _dispatch() {
      this.dispatchEvent(new CustomEvent('qty:change', {
        bubbles: true,
        detail: { value: this.value }
      }));
    }

    disconnectedCallback() {
      this.removeEventListener('click', this);
      if (this._input) this._input.removeEventListener('change', this);
    }
  }

  customElements.define('pdp-qty-stepper', PdpQtyStepper);
}

/**
 * PDP global loading bus — use one event for all loading UI sync (sticky bar, etc.).
 * @param {'atc'|'buy_now'} scope
 * @param {boolean} active - true=start loading, false=end
 */
function dispatchPdpLoading(scope, active) {
  document.dispatchEvent(new CustomEvent('pdp:loading', {
    bubbles: true,
    detail: { scope, active: !!active }
  }));
}

window.dispatchPdpLoading = dispatchPdpLoading;

if (!customElements.get('pdp-product-form')) {
  class PdpProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.variantInput = this.querySelector('[data-pdp-variant-input]');
      this.variants = JSON.parse(this.querySelector('[data-pdp-variants]').textContent);
      // 回填 Liquid json 过滤器不输出的库存字段
      this.querySelector('[data-pdp-variants-inventory]')?.querySelectorAll('option').forEach((opt) => {
        const v = this.variants.find((v) => String(v.id) === opt.value);
        if (!v) return;
        v.inventory_quantity = parseInt(opt.dataset.inventoryQuantity, 10) || 0;
        v.inventory_policy = opt.dataset.inventoryPolicy || 'deny';
        v.inventory_management = opt.dataset.inventoryManagement || null;
      });
      this.currentVariant = this.variants.find(v => String(v.id) === this.variantInput.value) || this.variants[0];

      this._bindSwatches();
      this._bindQuantity();
      this._bindForm();
      this._bindBuyNow();
      this._bindExternalEvents();

      /* Dispatch initial variant so PdpPrice, PdpCoupon, etc. get data on page load.
         Use queueMicrotask so all customElements.define() calls finish first
         and every listener (e.g. PdpPrice, defined later) is already registered. */
      queueMicrotask(() => this._dispatchVariantChanged(this.currentVariant, null, true));
    }

    disconnectedCallback() {
      if (this._optionChangeFrame) {
        window.cancelAnimationFrame(this._optionChangeFrame);
        this._optionChangeFrame = null;
      }
      if (this._optionChangeTimer) {
        window.clearTimeout(this._optionChangeTimer);
        this._optionChangeTimer = null;
      }
      document.removeEventListener('pdp:product-action', this._onExternalProductAction);
      if (this._pageShowHandler) {
        window.removeEventListener('pageshow', this._pageShowHandler);
      }
    }

    /* ── Swatch Selection ── */

    _bindSwatches() {
      this.querySelectorAll('[data-pdp-swatch]').forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          if (swatch.classList.contains('is-soldout')) return;

          const optionEl = swatch.closest('[data-pdp-option]');
          optionEl.querySelectorAll('[data-pdp-swatch]').forEach(s => {
            s.classList.remove('is-active');
            if (s.hasAttribute('aria-pressed')) s.setAttribute('aria-pressed', 'false');
          });
          swatch.classList.add('is-active');
          if (swatch.hasAttribute('aria-pressed')) swatch.setAttribute('aria-pressed', 'true');

          const currentValueEl = optionEl.querySelector('[data-pdp-current-value]');
          if (currentValueEl) currentValueEl.textContent = swatch.dataset.value;

          this._scheduleOptionChange();
        });
      });
    }

    _scheduleOptionChange() {
      if (this._optionChangeFrame) window.cancelAnimationFrame(this._optionChangeFrame);
      if (this._optionChangeTimer) window.clearTimeout(this._optionChangeTimer);

      this._optionChangeFrame = window.requestAnimationFrame(() => {
        this._optionChangeFrame = null;
        this._optionChangeTimer = window.setTimeout(() => {
          this._optionChangeTimer = null;
          this._onOptionChange();
        }, 0);
      });
    }

    _onOptionChange() {
      const optionEls = Array.from(this.querySelectorAll('[data-pdp-option]'));
      const selectedOptions = [];
      optionEls.forEach(optionEl => {
        const active = optionEl.querySelector('[data-pdp-swatch].is-active');
        if (active) {
          selectedOptions.push({
            index: parseInt(optionEl.dataset.optionIndex, 10),
            value: active.dataset.value,
            el: optionEl,
          });
        }
      });

      const oldVariant = this.currentVariant;
      let newVariant = this.variants.find(v =>
        selectedOptions.every(o => v['option' + (o.index + 1)] === o.value)
      );

      // If no exact match, find the closest available variant by relaxing
      // non-most-recently-changed options one at a time (fewest changes first).
      if (!newVariant) {
        // Try to find an available variant that matches as many of the selected
        // options as possible, preferring variants that differ in fewest options.
        const available = this.variants.filter(v => v.available);
        if (available.length) {
          // Score each available variant by how many selected options it satisfies
          let bestVariant = null;
          let bestScore = -1;
          for (const v of available) {
            const score = selectedOptions.reduce((acc, o) =>
              acc + (v['option' + (o.index + 1)] === o.value ? 1 : 0), 0);
            if (score > bestScore) {
              bestScore = score;
              bestVariant = v;
            }
          }
          newVariant = bestVariant;
        }

        if (!newVariant) return;

        // Update the DOM to reflect the auto-corrected option values
        optionEls.forEach(optionEl => {
          const optionIndex = parseInt(optionEl.dataset.optionIndex, 10);
          const correctedValue = newVariant['option' + (optionIndex + 1)];
          if (!correctedValue) return;

          const swatches = optionEl.querySelectorAll('[data-pdp-swatch]');
          swatches.forEach(s => {
            const isActive = s.dataset.value === correctedValue;
            s.classList.toggle('is-active', isActive);
            if (s.hasAttribute('aria-pressed')) s.setAttribute('aria-pressed', String(isActive));
          });

          const currentValueEl = optionEl.querySelector('[data-pdp-current-value]');
          if (currentValueEl) currentValueEl.textContent = correctedValue;
        });
      }

      this.currentVariant = newVariant;
      this.variantInput.value = newVariant.id;

      this._updateAtcButton(newVariant);
      this._updateSwatchAvailability();
      this._updateQuantityMax(newVariant);
      this._updateUrl(newVariant);

      this._dispatchVariantChanged(newVariant, oldVariant);
    }

    /* ── Availability Cross-Check ── */

    _updateSwatchAvailability() {
      const optionEls = Array.from(this.querySelectorAll('[data-pdp-option]'));

      optionEls.forEach(optionEl => {
        const optionIndex = parseInt(optionEl.dataset.optionIndex, 10);
        const swatches = optionEl.querySelectorAll('[data-pdp-swatch]');

        swatches.forEach(swatch => {
          const testValue = swatch.dataset.value;

          // First pass: check if this value is available when OTHER options are fixed
          // to their currently active values (strict cross-dimension check).
          const strictlyAvailable = this.variants.some(v => {
            if (v['option' + (optionIndex + 1)] !== testValue) return false;
            let matches = true;
            optionEls.forEach(otherEl => {
              const otherIndex = parseInt(otherEl.dataset.optionIndex, 10);
              if (otherIndex === optionIndex) return;
              const otherActive = otherEl.querySelector('[data-pdp-swatch].is-active');
              if (otherActive && v['option' + (otherIndex + 1)] !== otherActive.dataset.value) {
                matches = false;
              }
            });
            return matches && v.available;
          });

          if (strictlyAvailable) {
            swatch.classList.remove('is-soldout', 'is-cross');
            return;
          }

          // Second pass (fallback): check if this value has ANY available variant
          // across all option combinations. If yes, mark as cross (clickable with hint)
          // so the user can trigger auto-correction to that combination.
          const anyAvailable = this.variants.some(v =>
            v['option' + (optionIndex + 1)] === testValue && v.available
          );

          if (anyAvailable) {
            swatch.classList.remove('is-soldout');
            swatch.classList.add('is-cross');
          } else {
            swatch.classList.remove('is-cross');
            swatch.classList.add('is-soldout');
          }
        });
      });
    }

    /* ── ATC Button State ── */

    _updateAtcButton(variant) {
      const btn = this.querySelector('[data-pdp-atc]');
      const textEl = this.querySelector('[data-pdp-atc-text]');
      const dotEl = this.querySelector('[data-pdp-atc-dot]');
      if (!btn) return;

      const addText = btn.closest('form')?.dataset?.addToCartText || 'Add to cart';

      if (!variant || !variant.available) {
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('disabled', '');
        if (textEl) textEl.textContent = window.variantStrings?.soldOut || 'Sold out';
        if (dotEl) dotEl.hidden = true;
      } else {
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('disabled');
        if (textEl) textEl.textContent = addText;
        if (dotEl) dotEl.hidden = false;
      }

      const buyNowBtn = this.querySelector('[data-pdp-buy-now]');
      if (buyNowBtn) {
        if (!variant || !variant.available) {
          buyNowBtn.setAttribute('aria-disabled', 'true');
          buyNowBtn.setAttribute('disabled', '');
          buyNowBtn.classList.add('is-disabled');
        } else {
          buyNowBtn.removeAttribute('aria-disabled');
          buyNowBtn.removeAttribute('disabled');
          buyNowBtn.classList.remove('is-disabled');
        }
      }

      const shopifyPaymentBtn = this.querySelector('.pdp-pf__shop-pay');
      if (shopifyPaymentBtn) {
        if (!variant || !variant.available) {
          shopifyPaymentBtn.setAttribute('aria-disabled', 'true');
          shopifyPaymentBtn.setAttribute('disabled', '');
          shopifyPaymentBtn.classList.add('is-disabled');
        } else {
          shopifyPaymentBtn.removeAttribute('aria-disabled');
          shopifyPaymentBtn.removeAttribute('disabled');
          shopifyPaymentBtn.classList.remove('is-disabled');
        }
      }
    }

    /* ── Quantity ── */

    _bindQuantity() {
      this._qtyStepper = this.querySelector('pdp-qty-stepper');
    }

    _updateQuantityMax(variant) {
      if (!this._qtyStepper || !variant) return;

      let max = 9999;
      if (variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_policy !== 'continue') {
        max = variant.inventory_quantity;
      }

      this._qtyStepper.setAttribute('max', max);
    }

    /* ── Form Submit ── */

    _bindForm() {
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const btn = this.querySelector('[data-pdp-atc]');
        if (btn && (btn.hasAttribute('disabled') || btn.getAttribute('aria-disabled') === 'true')) return;
        this._submitAjaxCart(btn);
      });
    }

    _bindBuyNow() {
      const btn = this.querySelector('[data-pdp-buy-now]');
      if (!btn) return;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (btn.getAttribute('aria-disabled') === 'true' || btn.hasAttribute('disabled')) return;
        this.buyNow();
      });

      this._pageShowHandler = (e) => {
        if (!e.persisted) return;
        this._setBuyNowLoading(false);
      };
      window.addEventListener('pageshow', this._pageShowHandler);
    }

    _setBuyNowLoading(isLoading) {
      const btn = this.querySelector('[data-pdp-buy-now]');
      if (!btn) return;
      const spinner = btn.querySelector('[data-pdp-buy-now-spinner]');
      if (spinner) spinner.classList.toggle('is-active', isLoading);
      btn.classList.toggle('is--loading', isLoading);
      dispatchPdpLoading('buy_now', isLoading);
      if (isLoading) {
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('disabled', '');
      } else {
        this._updateAtcButton(this.currentVariant);
      }
    }

    _setBtnLoading(btn) {
      const atcBtn = this.querySelector('[data-pdp-atc]');
      if (atcBtn) {
        if (btn === atcBtn || btn === "atc") atcBtn.classList.add('is-loading');
        atcBtn.setAttribute('aria-disabled', 'true');
      }
      const buyNowBtn = this.querySelector('[data-pdp-buy-now]');
      if (buyNowBtn) {
        if (btn === buyNowBtn || btn === "buy-now") buyNowBtn.classList.add('is-loading');
        buyNowBtn.setAttribute('aria-disabled', 'true');
      }

      const shopifyPaymentBtn = this.querySelector('.pdp-pf__shop-pay');
      if (shopifyPaymentBtn) {
        shopifyPaymentBtn.setAttribute('aria-disabled', 'true');
        shopifyPaymentBtn.setAttribute('disabled', '');
        shopifyPaymentBtn.classList.add('is-disabled');
      }
      dispatchPdpLoading('atc', true);
    }

    _clearBtnLoading() {
      const atcBtn = this.querySelector('[data-pdp-atc]');
      if (atcBtn) {
        atcBtn.classList.remove('is-loading');
        atcBtn.removeAttribute('aria-disabled');
      }
      const buyNowBtn = this.querySelector('[data-pdp-buy-now]');
      if (buyNowBtn) {
        buyNowBtn.classList.remove('is-loading');
        buyNowBtn.removeAttribute('aria-disabled');
      }
      const shopifyPaymentBtn = this.querySelector('.pdp-pf__shop-pay');
      if (shopifyPaymentBtn) {
        shopifyPaymentBtn.removeAttribute('aria-disabled');
        shopifyPaymentBtn.removeAttribute('disabled');
        shopifyPaymentBtn.classList.remove('is-disabled');
      }
      dispatchPdpLoading('atc', false);
    }


    _submitAjaxCart(btn, flySourceEl) {
      /* Loading state (matches T4S is--loading / aria-disabled pattern) */
      this._setBtnLoading(btn);

      /* jQuery ref for T4S event triggers */
      const $ = window.jQuery_T4NT || window.jQuery;
      const $doc = $ ? $(document) : null;

      const clearLoading = () => {
        this._clearBtnLoading(btn);
      }

      const notifyCartChanged = (source, cartData) => {
        const detail = { source };
        if (cartData && typeof cartData === 'object') detail.item = cartData;

        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
      };

      const notifySuccess = (data) => {
        notifyCartChanged('pdp', data);
        if ($doc) $doc.trigger('add:cart:success').trigger('add:cart:upsell');
        if (typeof T4SThemeSP !== 'undefined') {
          T4SThemeSP.isATCSuccess = true;
          this._flyToCart(btn, flySourceEl);
        }
      };

      const notifyError = (data) => {
        if (data && data.description && !window.T4Sconfigs?.disATCerror && typeof T4SThemeSP !== 'undefined') {
          T4SThemeSP.Notices(data.description);
        }
        if ($doc) $doc.trigger('cart:updated', ['error']);
        else clearLoading();
      };

      /* Listen for cart:updated to clear loading (T4S pattern) */
      if ($doc) {
        $doc.on('cart:updated.pdpForm', () => {
          clearLoading();
          $doc.off('cart:updated.pdpForm');
        });
      }

      const addUrl = (window.T4Sroutes?.cart_add_url || '/cart/add') + '.js';

      const onFetchError = (err) => {
        console.error('PDP ATC error:', err);
        if ($doc) $doc.trigger('cart:updated');
        else clearLoading();
      };

      const formData = new FormData(this.form);

      /* ── Check for buy-get-gift on the page ── */
      const bgg = document.querySelector('buy-get-gift');
      if (bgg) {
        bgg.onSubmit(this, { formData, onSuccess: notifySuccess, onError: notifyError, onFetchError, clearLoading: clearLoading });
        return;
      }

      this._cartAdd(addUrl, formData, { onSuccess: notifySuccess, onError: notifyError, onFetchError });
    }

    /**
     * POST form data to /cart/add.js, appending T4S section keys automatically.
     * @param {string} url
     * @param {FormData} formData
     * @param {{ onSuccess: Function, onError: Function, onFetchError: Function }} handlers
     */
    _cartAdd(url, formData, { onSuccess, onError, onFetchError }) {
      const cartType = window.T4Sconfigs?.cartType;
      const sectionsStr = cartType === 'disable'
        ? 'cart_data'
        : cartType === 'cart'
          ? `cart_data,${window.cartT4SectionID}`
          : 'cart_data,mini_cart';

      sectionsStr.split(',').filter(Boolean).forEach((s) => formData.append('sections[]', s));
      formData.append('sections_url', window.location.pathname);

      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'x-b2g-internal': 'true' },
        body: formData,
      })
        .then((res) => res.json())
        .then((data) => { if (data.status) { onError(data); return; } onSuccess(data); })
        .catch(onFetchError);
    }

    /* ── URL Update ── */

    _updateUrl(variant) {
      if (!variant || !window.history.replaceState) return;
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    /* ── Public API ── */

    addToCart(flySourceEl) {
      const btn = this.querySelector('[data-pdp-atc]');
      if (btn && (btn.hasAttribute('disabled') || btn.getAttribute('aria-disabled') === 'true')) return;
      this._submitAjaxCart(btn, flySourceEl);
    }

    async buyNow() {
      if (!this.currentVariant?.available) return;
      this._setBtnLoading("buy-now");

      const quantity = this._getQuantity();
      const productId = this.dataset.productId;
      const items = [{
        id: this.currentVariant.id,
        quantity,
        product_id: productId,
      }];
      let lines = this._getStorefrontLines(items);

      const bgg = document.querySelector('buy-get-gift');
      if (bgg) {
        const that = this;
        bgg.onBuyNow({
          id: this.currentVariant.id,
          quantity,
        }, { clearLoading: () => that._clearBtnLoading() });
        return
      }

      try {
        const checkoutUrl = await this._createBuyNowStorefrontCart(lines);
        if (!checkoutUrl) throw new Error('Storefront cartCreate did not return checkoutUrl');
        window.location.href = checkoutUrl;
      } catch (error) {
        console.error('PDP Buy Now Storefront Cart error:', error);
      } finally {
        this._clearBtnLoading();
      }
    }

    _createBuyNowStorefrontCart(lines) {
      if (!lines.length) return Promise.resolve(null);

      const countryCode = window.__pdp__config__?.countryCode || 'ZZ';

      const query = `mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            id
            checkoutUrl
          }
          userErrors {
            field
            message
          }
          warnings {
            message
          }
        }
      }`;

      return fetch('/api/2026-10/graphql.json', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          variables: {
            input: {
              lines,
              buyerIdentity: {
                countryCode,
              },
            },
          },
        }),
      }).then((response) => {
        if (!response.ok) throw response;
        return response.json();
      }).then((result) => {
        const payload = result && result.data && result.data.cartCreate;
        const errors = (result && result.errors) || (payload && payload.userErrors) || [];

        if (errors.length) {
          throw new Error(errors.map((error) => error.message).filter(Boolean).join('; ') || 'Storefront cartCreate failed');
        }

        return payload && payload.cart && payload.cart.checkoutUrl ? payload.cart.checkoutUrl : null;
      });
    }

    _getStorefrontLines(items) {
      const lines = []
      if (!items || !Array.isArray(items)) return lines;
      for (const item of items) {
        const variantId = Number(item && (item.id || item.variant_id));
        const quantity = Number(item && item.quantity) || 1;

        if (!variantId || quantity <= 0) continue;

        const line = {
          merchandiseId: this._getStorefrontVariantGid(variantId),
          quantity,
        };

        const attributes = this._getBuyNowStorefrontAttributes(item && item.properties);
        if (attributes.length) line.attributes = attributes;

        lines.push(line);
      }

      return lines;
    }

    _getBuyNowStorefrontAttributes(properties) {
      return Object.keys(properties || {}).reduce((attributes, key) => {
        if (properties[key] == null || properties[key] === '') return attributes;

        attributes.push({
          key,
          value: String(properties[key]),
        });

        return attributes;
      }, []);
    }

    _getStorefrontVariantGid(variantId) {
      return `gid://shopify/ProductVariant/${variantId}`;
    }

    _getQuantity() {
      return this._qtyStepper ? this._qtyStepper.value : 1;
    }

    _flyToCart(btn, flySourceEl) {
      let trigger = flySourceEl || btn;
      const section = this.closest('.shopify-section, [data-section-id]');
      const domImg = section?.querySelector('.swiper-slide-active img') ?? section?.querySelector('.swiper-slide img');


      if (T4SThemeSP.Cart.isElementMostlyInViewport(domImg)) trigger = domImg;
      const doFly = (imgSrc) => {
        T4SThemeSP.Header.revealAsync().then(() => {
          T4SThemeSP.Cart.flyToCart(trigger, imgSrc);
          T4SThemeSP.Cart.getToFetch();
        });
      };

      const src = flySourceEl && this.currentVariant?.featured_image?.src;
      if (src) {
        const img = new Image();
        img.onload = () => doFly(src);
        img.onerror = () => doFly(domImg);
        img.src = src;
      } else {
        doFly(domImg);
      }
    }

    _bindExternalEvents() {
      this._onExternalProductAction = (e) => {
        const action = e.detail && e.detail.action;
        if (action === 'atc') this.addToCart(e.target);
        else if (action === 'buy_now') this.buyNow();
      };
      document.addEventListener('pdp:product-action', this._onExternalProductAction);
    }

    /* ── Variant Event Dispatch ── */

    _dispatchVariantChanged(newVariant, oldVariant, isInit = false) {
      const event = new CustomEvent('variant:changed', {
        bubbles: true,
        detail: newVariant
      });
      event.currentVariant = newVariant;
      event.oldVariant = oldVariant;
      event.isInit = isInit;
      event.quantity = this._getQuantity();
      this.form.dispatchEvent(event);
    }
  }

  customElements.define('pdp-product-form', PdpProductForm);
}

/* ===== PDP Price — Auto-update on variant change ===== */
if (!customElements.get('pdp-price')) {
  class PdpPrice extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._coupon = null;
      this._member = null;
      this._lastVariant = null;
      this._onVariantChanged = this._onVariantChanged.bind(this);
      this._onCouponApplied = this._onCouponApplied.bind(this);
      this._onCouponRemoved = this._onCouponRemoved.bind(this);
      this._onMemberPriceChange = this._onMemberPriceChange.bind(this);
      document.addEventListener('variant:changed', this._onVariantChanged);
      document.addEventListener('pdp:coupon-applied', this._onCouponApplied);
      document.addEventListener('pdp:coupon-removed', this._onCouponRemoved);
      document.addEventListener('pdp:member-price-change', this._onMemberPriceChange);

      /* 旧版 PDP 通过 jQuery.trigger('variant:changed') 派发事件，不触发原生 addEventListener。
         额外绑 jQuery 监听确保旧版 PDP 变体切换时 _lastVariant 能正确更新。 */
      const $ = window.jQuery_T4NT || window.jQuery;
      if ($ && $.fn && $.fn.on) {
        this._jqVariantChanged = (evt) => this._onVariantChanged(evt);
        $(document).on('variant:changed', this._jqVariantChanged);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('variant:changed', this._onVariantChanged);
      document.removeEventListener('pdp:coupon-applied', this._onCouponApplied);
      document.removeEventListener('pdp:coupon-removed', this._onCouponRemoved);
      document.removeEventListener('pdp:member-price-change', this._onMemberPriceChange);
      if (this._jqVariantChanged) {
        const $ = window.jQuery_T4NT || window.jQuery;
        if ($ && $.fn && $.fn.off) $(document).off('variant:changed', this._jqVariantChanged);
      }
    }

    _onCouponApplied(e) {
      this._coupon = e.detail;
      this._render();
    }

    _onCouponRemoved() {
      this._coupon = null;
      this._render();
    }

    _onMemberPriceChange(e) {
      this._member = e.detail || null;
      this._render();
    }

    _onVariantChanged(e) {
      const variant = e.currentVariant || (e.detail && e.detail.variant);
      if (!variant) return;
      // 去重：jQuery + 原生双路径时同一 variant 只处理一次
      if (this._lastVariant && String(variant.id) === String(this._lastVariant.id)) return;
      const isInit = !this._lastVariant;
      this._lastVariant = variant;
      /* 状态清理边界（§4.3.3）：切换 variant 时丢弃上一 variant 的会员价与折扣码残留，
         渲染基础价，等最新 pdp:member-price-change / pdp:coupon-applied 事件重新叠加。
         折扣码 UI variant 的重新勾选由 PdpCoupon 在 variant:changed 时按新 variant 重新派发；
         此处清空 _coupon 是为了避免旧 variant 的折扣码串入会员价 UI variant。 */
      this._member = null;
      this._coupon = null;
      if (!isInit) this._render();
    }

    _applyDiscount(price) {
      if (!this._coupon) return price;
      const { discountType, discountValue } = this._coupon;
      if (discountType === 'percentage') {
        return Math.ceil(price * (1 - discountValue / 100));
      }
      return Math.max(0, price - discountValue * 100);
    }

    _render() {
      const variant = this._lastVariant;

      if (!variant) return;

      const fmt = (cents, format) => T4SThemeSP.Currency?.formatMoney(cents, format) || '';
      const compareFormat = this.dataset.compareFormat || window.__pdp__config__?.moneyFormat;

      /* 最终价解析：会员价 > 折扣码 > 基础价（最后生效规则）。
         member-price.js 只负责算价并派发 pdp:member-price-change（applyMember=false 表示该 variant 不使用会员价）；
         isMemberVariant 表示当前 variant 属于会员价 UI（member-price.js 派发时带出）：
         会员价 UI variant 上即使未勾选折扣（applyMember=false）也只显示基础价，
         绝不叠加上一 variant 残留的折扣码；折扣码仅对折扣码 UI variant 生效。 */
      const m = this._member;
      const applyMember = !!(m && m.applyMember);
      const isMemberVariant = !!(m && m.isMemberVariant);
      const finalPrice = applyMember
        ? m.finalPrice
        : (isMemberVariant ? variant.price : this._applyDiscount(variant.price));

      const priceEl = this.querySelector('[data-pdp-current-price]');
      if (priceEl) priceEl.textContent = fmt(finalPrice);

      /* compare price: 仅当 compare_at_price 为有效划线价（高于现价）时优先展示；
         无效（低于/等于现价）或不存在时，勾选折扣则以原价作为划线价 */
      const compareEl = this.querySelector('[data-pdp-compare-price]');
      const hasValidCompare = variant.compare_at_price != null && variant.compare_at_price > variant.price;
      const compareValue = hasValidCompare ? variant.compare_at_price : (finalPrice < variant.price ? variant.price : null);
      const hasCompare = compareValue && compareValue > finalPrice;
      if (hasCompare) {
        if (compareEl) {
          compareEl.textContent = fmt(compareValue, compareFormat);
          compareEl.hidden = false;
        } else if (priceEl) {
          const del = document.createElement('del');
          del.className = 'pdp-product__compare-price pdp-tmp-compare';
          del.setAttribute('data-pdp-compare-price', '');
          del.textContent = fmt(compareValue, compareFormat);
          priceEl.after(del);
        }
      } else if (compareEl) {
        if (compareEl.classList.contains('pdp-tmp-compare')) {
          compareEl.remove();
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }

      const atcPriceEl = this.querySelector('[data-pdp-atc-price]');
      if (atcPriceEl) atcPriceEl.textContent = fmt(finalPrice);

      /* Saving badge：会员价或折扣码生效时展示节省金额，使用 Liquid 预置的 [data-pdp-saving-badge] 节点 */
      /* 文案与 member-price.js 统一：__MP__.i18n.badgeSave（products.badge.save_amount_fixed_2_html），
         货币代码随 settings.currency_code_enabled（__MP__.currencyCodeEnabled） */
      const badgeEl = this.querySelector('[data-pdp-saving-badge]');
      const badgeTextEl = badgeEl && badgeEl.querySelector('[data-pdp-saving-text]');
      // badge 展示条件：
      //   1. 商品原始划线价（hasValidCompare）：finalPrice < compareValue 时始终展示，无需折扣生效
      //   2. 临时划线价（折扣/会员价生效，以原价作划线）：需要折扣确实生效（applyMember 或 coupon 勾选）
      const discountActive = applyMember || (!isMemberVariant && this._coupon);
      const shouldShowBadge = hasCompare && (hasValidCompare || discountActive);
      if (shouldShowBadge) {
        const savingCents = compareValue - finalPrice;
        if (badgeEl && savingCents > 0) {
          const _MP = window.__MP__ || {};
          const _cfg = window.__pdp__config__ || {};
          const badgeTpl = (_MP.i18n && _MP.i18n.badgeSave) || 'SAVE [sale]';
          // const badgeFormat = (_MP.currencyCodeEnabled && _MP.moneyWithCurrencyFormat) ? _MP.moneyWithCurrencyFormat : _cfg.moneyFormat; // 取消货币format
          const badgeFormat = _cfg.moneyFormat; // 取消货币format
          const badgeText = badgeTpl.replace('[sale]', fmt(savingCents, badgeFormat));
          if (badgeTextEl) {
            badgeTextEl.textContent = badgeText;
          } else {
            // 旧版 PDP（main-product.liquid）：[data-pdp-saving-badge] 自身就是文本容器，无 [data-pdp-saving-text] 子节点
            badgeEl.textContent = badgeText;
          }
          badgeEl.hidden = false;
        }
      } else if (badgeEl) {
        if (badgeTextEl) badgeTextEl.textContent = '';
        else badgeEl.textContent = '';
        badgeEl.hidden = true;
      }

      this.dispatchEvent(new CustomEvent('pdp:price-change', {
        bubbles: true,
        detail: { price: finalPrice, variant, coupon: this._coupon, member: m }
      }));
    }
  }

  customElements.define('pdp-price', PdpPrice);
}

/* ── Tooltip (generic) ── */
if (!customElements.get('pdp-tooltip')) {
  class PdpTooltip extends HTMLElement {
    connectedCallback() {
      this._trigger = this.querySelector('[data-tooltip-trigger]');
      this._content = this.querySelector('[data-tooltip-content]');
      if (!this._trigger || !this._content) return;

      this._trigger.addEventListener('click', () => {
        const opening = !this._content.classList.contains('is-open');
        this._content.classList.toggle('is-open');
        if (opening) this._position();
      });

      this._onOutsideClick = (e) => {
        if (!this.contains(e.target)) this.close();
      };
      document.addEventListener('click', this._onOutsideClick);
    }

    disconnectedCallback() {
      if (this._onOutsideClick) document.removeEventListener('click', this._onOutsideClick);
    }

    close() {
      if (this._content) this._content.classList.remove('is-open');
    }

    /** Reposition tooltip so it stays within the viewport */
    _position() {
      const s = this._content.style;
      /* reset to natural position so we can measure */
      s.removeProperty('--tt-left');
      s.removeProperty('--tt-right');

      const rect = this._content.getBoundingClientRect();
      const pad = 8; /* minimum gap from viewport edge */

      if (rect.right > window.innerWidth - pad) {
        /* overflows right → anchor to right edge of trigger */
        s.setProperty('--tt-right', '0');
        s.setProperty('--tt-left', 'auto');
      } else if (rect.left < pad) {
        /* overflows left → anchor to left edge of trigger */
        s.setProperty('--tt-left', '0');
        s.setProperty('--tt-right', 'auto');
      }
    }
  }

  customElements.define('pdp-tooltip', PdpTooltip);
}

/* ── Ship To ── */
if (!customElements.get('pdp-ship-to')) {
  class PdpShipTo extends HTMLElement {
    connectedCallback() {
      this._trigger = this.querySelector('[data-ship-trigger]');
      this._dropdown = this.querySelector('[data-ship-dropdown]');
      this._currentLabel = this.querySelector('[data-ship-current]');
      this._form = this.querySelector('#pdp-ship-to-form');
      this._countryInput = this.querySelector('[data-ship-country-input]');

      if (this._trigger) {
        this._trigger.addEventListener('click', () => this._toggleDropdown());
      }
      if (this._dropdown) {
        this._dropdown.addEventListener('click', (e) => {
          const opt = e.target.closest('[data-ship-option]');
          if (opt) this._selectCountry(opt);
        });
      }

      /* Close dropdown when tooltip inside is clicked */
      this.addEventListener('click', (e) => {
        if (e.target.closest('pdp-tooltip')) this._closeDropdown();
      });

      this._onOutsideClick = (e) => {
        if (!this._trigger.contains(e.target)) this._closeDropdown();
      };
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onOutsideClick);
    }

    _toggleDropdown() {
      const isOpen = this._dropdown.classList.toggle('is-open');
      this._trigger.setAttribute('aria-expanded', String(isOpen));
      this.querySelector('pdp-tooltip')?.close();
      if (isOpen) {
        document.addEventListener('click', this._onOutsideClick);
      } else {
        document.removeEventListener('click', this._onOutsideClick);
      }
    }

    _closeDropdown() {
      if (this._dropdown) {
        this._dropdown.classList.remove('is-open');
        this._trigger.setAttribute('aria-expanded', 'false');
      }
      document.removeEventListener('click', this._onOutsideClick);
    }

    _selectCountry(opt) {
      const code = opt.dataset.countryCode;
      if (!code || !this._form || !this._countryInput) return;

      this._dropdown.querySelectorAll('[data-ship-option]').forEach((o) => {
        o.classList.remove('is-active');
        o.setAttribute('aria-selected', 'false');
      });
      opt.classList.add('is-active');
      opt.setAttribute('aria-selected', 'true');
      this._currentLabel.textContent = opt.textContent.trim();
      this._closeDropdown();

      this._countryInput.value = code;
      this._form.submit();
    }
  }

  customElements.define('pdp-ship-to', PdpShipTo);
}